import { useState } from "react";
import {
  type FieldValues,
  type Path,
  useFormContext,
  type UseFormReturn,
} from "react-hook-form";

import { RoundSwitch } from "@/components/atoms";
import { FormControl, FormField, FormItem } from "@/components/ui/form";

interface RatingSwitchGroupProps<T extends FieldValues> {
  taste: string;
  form: UseFormReturn<T>;
  rating: number;
  disabled?: boolean;
}

const RatingSwitchGroup = <T extends FieldValues>({
  form,
  taste,
  rating,
  disabled = false,
}: RatingSwitchGroupProps<T>) => {
  const { setValue, getValues } = useFormContext();
  const [selectedRating, setSelectedRating] = useState<number>(rating);
  const RATINGS = "ratings";

  const handleRatingChange = (num: number) => {
    setSelectedRating(num);
    const currentRatings = getValues(RATINGS) || {};
    currentRatings[taste] = num;

    setValue(RATINGS, {
      ...currentRatings,
    });
  };

  return (
    <div className="flex flex-col gap-[6px]">
      <p className="text-sm">{taste}</p>
      <div className="flex items-center gap-[8px]">
        {[1, 2, 3, 4, 5].map(num => (
          <FormField
            key={num}
            control={form.control}
            name={`${taste}.${num}` as Path<T>}
            render={() => (
              <FormItem>
                <FormControl>
                  <RoundSwitch
                    key={num}
                    checked={num <= selectedRating}
                    onCheckedChange={() => handleRatingChange(num)}
                    data-testid={`rating-switch-${taste}-${num}`}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default RatingSwitchGroup;
